import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import useSWR from 'swr'
import { api } from '../services/api'
import { useDebounce } from '../hooks'
import { Button, Pagination } from '../components'
import pageStyles from '../styles/page.module.scss'
import listTableStyles from '../styles/list-table.module.scss'
import buttonStyles from '../components/Button.module.scss'
import styles from './ComplexesList.module.scss'

interface Complex {
  tc_complex_id: string
  ext_complex_id: string
  tc_name: string
  ext_name: string
  sport_name: string
  catalog_source_name: string
  matched_on?: string | null  // Shows why this result matched (e.g., "Court: Rod Laver Arena")
  created_at: string
  updated_at: string
}

interface ComplexesResponse {
  complexes: Complex[]
  pagination: {
    page: number
    limit: number
    total_count: number
    total_pages: number
    has_next: boolean
    has_prev: boolean
  }
  filters: {
    name: string
  }
  timestamp: string
}

const ComplexesList: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const pageSize = 100

  const debouncedSearchTerm = useDebounce(searchTerm, 300)

  const { data, isLoading, error, mutate } = useSWR(
    ['complexes', { page: currentPage, limit: pageSize, name: debouncedSearchTerm }],
    () => api.get('/complexes', { page: currentPage, limit: pageSize, name: debouncedSearchTerm?.trim() || undefined }),
    { revalidateOnFocus: false }
  )

  // Reset to first page when search term changes
  useEffect(() => {
    setCurrentPage(1)
  }, [debouncedSearchTerm])

  const clearSearch = () => {
    setSearchTerm('')
  }

  if (error) {
    return (
      <div className={pageStyles.pageContainer}>
        <div className={pageStyles.error}>
          <h2>Error loading complexes</h2>
          <p>Failed to fetch complexes: {(error as Error).message}</p>
          <Button variant="danger" onClick={() => mutate()}>Retry</Button>
        </div>
      </div>
    )
  }

  const complexesData: ComplexesResponse = data?.body ? JSON.parse(data.body) : data
  const complexes = complexesData?.complexes || []
  const pagination = complexesData?.pagination

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A'
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    } catch {
      return 'N/A'
    }
  }

  const getComplexName = (complex: Complex) => {
    return complex.tc_name || complex.ext_name || 'Unknown Complex'
  }

  const getExternalId = (complex: Complex) => {
    return complex.ext_complex_id || 'N/A'
  }

  return (
    <div className={pageStyles.pageContainer}>
      {/* Header Button */}
      <div className={pageStyles.headerButtons}>
        <Link
          to="/courts/new"
          className={`${buttonStyles.button} ${buttonStyles.primary} ${buttonStyles.medium}`}
        >
          + New Complex
        </Link>
      </div>

      <div className={pageStyles.pageHeader}>
        <h1>Complexes & Courts</h1>
      </div>

      {/* Search and Filters */}
      <div className={listTableStyles.searchSection}>
        <div className={listTableStyles.searchForm}>
          <div className={listTableStyles.searchInputGroup}>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by complex name, court name, or city..."
              className={listTableStyles.searchInput}
            />
            {searchTerm && (
              <button type="button" onClick={clearSearch} className={listTableStyles.clearButton}>
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {isLoading && !data ? (
        <div className={pageStyles.loading}>
          <h3>Loading complexes...</h3>
          <p>Please wait while we fetch the complex data.</p>
        </div>
      ) : complexes.length === 0 ? (
        <div className={listTableStyles.noResults}>
          <h3>No complexes found</h3>
          <p>
            {debouncedSearchTerm
              ? `No complexes match "${debouncedSearchTerm}". Try searching by complex name, court name, or city.`
              : 'Unable to load complex data.'
            }
          </p>
        </div>
      ) : (
        <>
          <div className={`${listTableStyles.tableHeader} ${styles.listHeader}`}>
            <div>Name</div>
            <div>External ID</div>
            <div>Updated</div>
            <div>Actions</div>
          </div>

          <div className={listTableStyles.tableBody}>
            {complexes.map((complex) => (
              <div key={complex.tc_complex_id} className={`${listTableStyles.tableRow} ${styles.listItem}`}>
                <div className={listTableStyles.cellPrimary}>
                  <p className={listTableStyles.primaryText}>
                    {getComplexName(complex)}
                  </p>
                  {complex.matched_on && (
                    <p className={listTableStyles.secondaryText} style={{ fontStyle: 'italic', opacity: 0.8 }}>
                      ↳ {complex.matched_on}
                    </p>
                  )}
                  {!complex.matched_on && complex.ext_name && complex.ext_name !== complex.tc_name && (
                    <p className={listTableStyles.secondaryText}>{complex.ext_name}</p>
                  )}
                </div>

                <div className={listTableStyles.cellId}>
                  {getExternalId(complex)}
                </div>

                <div className={listTableStyles.cellText}>
                  {formatDate(complex.updated_at)}
                </div>

                <div className={listTableStyles.cellActions}>
                  <Link
                    to={`/courts/${complex.tc_complex_id}`}
                    className={`${buttonStyles.button} ${buttonStyles.primary} ${buttonStyles.medium}`}
                  >
                    View
                  </Link>
                </div>
              </div>
            ))}
          </div>

          {pagination && (
            <Pagination
              pagination={pagination}
              onPageChange={setCurrentPage}
              itemName="complexes"
            />
          )}
        </>
      )}
    </div>
  )
}

export default ComplexesList
